/*
  Norse Quiz — the quiz engine.

  <norse-quiz> is a full-viewport overlay that sits on top of the results page it
  shares a URL with. Finishing it writes the answers through
  assets/norse-quiz-state.js and closes, revealing a results page that has
  already personalised itself. Nothing here knows anything about acne, scars or
  products: every question, option, rule and string arrives as configuration from
  the section schema, so a new quiz is a new page and new blocks, not new code.

  Deliberately NOT shared with croev-quiz-v1: that engine hard-codes its own
  domain logic (age maps, problem labels) and finishes by redirecting to a
  separate landing page. This one must not disturb it.
*/
(function () {
  'use strict';

  if (customElements.get('norse-quiz')) return;

  var OPEN_ATTRIBUTE = 'data-norse-quiz-open';
  var ID_ATTRIBUTE = 'data-norse-quiz-id';
  var AUTO_ADVANCE_MS = 250;

  /*
    A multi-select question that has filled its allowance can advance on its own, the
    way a single-select one does. Longer than AUTO_ADVANCE_MS on purpose: the shopper
    has just made their third pick and needs long enough to see it land before the
    screen changes under them.

    Both this and the blocking behaviour are opt-in per question — the quizzes built
    before them roll the oldest selection out instead, and must keep doing so.
  */
  var MAX_ADVANCE_MS = 420;

  /*
    The email step submits the customer form for real and the page navigates — see
    `_submitEmail` for why it cannot be a fetch. These two numbers shape how invisible
    that is:

    SIGNUP_SUBMIT_MS  how long the analysing screen is on screen before the submit goes
                      out. Long enough to be painted with its bar moving, and well
                      inside the screen's own duration so it never finishes first.
    SIGNUP_HOLD_MS    the analysing screen's timer while that submit is in flight. Far
                      longer than any redirect, so a submission that never leaves ends
                      with the shopper on their results rather than stuck for ever.
  */
  var SIGNUP_SUBMIT_MS = 900;
  var SIGNUP_HOLD_MS = 12000;

  /*
    Below this much left of the analysing screen, resuming skips it entirely and the page
    paints on the results. hCaptcha usually holds the submit for longer than the screen
    lasts, so this is the common path, not the edge case.
  */
  var RESUME_MIN_MS = 400;

  /** Fire-and-forget analytics. Both sinks are optional and never block the UI. */
  function track(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
    } catch (error) {
      /* no-op */
    }

    try {
      if (typeof window.cvg === 'function') {
        window.cvg({ method: 'track', eventName: eventName, properties: payload || {} });
      }
    } catch (error) {
      /* no-op */
    }
  }

  customElements.define(
    'norse-quiz',
    class NorseQuizElement extends HTMLElement {
      connectedCallback() {
        this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-nq-step]'));
        if (this.steps.length === 0) return;

        this.progressBar = this.querySelector('[data-nq-progress-bar]');
        this.progressWrap = this.querySelector('[data-nq-progress]');
        this.barBack = this.querySelector('[data-nq-bar-back]');
        this.barSkip = this.querySelector('[data-nq-bar-skip]');

        this.index = 0;
        this.history = [];
        this.answers = {};
        this.tokens = {};
        this.timers = [];
        this.started = false;
        this.counterLabel = this.dataset.nqCounterLabel || '[current] of [total]';

        // How many steps carry a number in the "X of Y" counter. The progress
        // bar is step/questionCount, agreed with the team because the Figma
        // widths are eyeballed and disagree between breakpoints.
        this.questionSteps = this.steps.filter(function (step) {
          return step.dataset.nqCounts === 'true';
        });

        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onSubmit = this._onSubmit.bind(this);

        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKeydown);
        /*
          Capture, not bubble. Shopify's bot protection attaches its own submit handling
          to a `{% form 'customer' %}`, and a listener on the form runs before a bubbling
          one up here — it then finishes the submission with `form.submit()`, which
          dispatches no submit event at all, so nothing can stop the page from being
          thrown out to `?customer_posted=true`. Capture on this element runs first.
        */
        this.addEventListener('submit', this._onSubmit, true);

        this._restore();
        this._decideInitialVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKeydown);
        this.removeEventListener('submit', this._onSubmit, true);
        this._clearTimers();
        this._clearTestimonials();
        this._setOpen(false);
      }

      /* ------------------------------------------------------------ open/close */

      /**
       * The quiz and the results page share a URL, so something has to decide
       * which of the two the visitor sees. A finished run in this session means
       * they have earned the results; `?quiz=1` always reopens so a campaign or a
       * "retake" link can force it.
       */
      _decideInitialVisibility() {
        var forced = new URLSearchParams(window.location.search).get('quiz');
        var saved = window.NorseQuiz ? window.NorseQuiz.read() : null;

        if (forced === '0') {
          this.close({ silent: true });
          return;
        }

        /*
          Back from the signup redirect with the analysing screen already spent: finish
          without ever opening, so the page paints on the results instead of replaying an
          animation the shopper has just watched.
        */
        if (this.resumeStraightToResults) {
          this._complete();
          return;
        }

        if (forced === '1' || !saved || !saved.completed) {
          this.open();
          return;
        }

        this.close({ silent: true });
      }

      open() {
        this.hidden = false;
        this._setOpen(true);
        this._show(this.index);

        if (!this.started) {
          this.started = true;
          track('quiz_started', { quiz_id: this.dataset.nqQuizId || '' });
        }
      }

      close(options) {
        this.hidden = true;
        this._setOpen(false);
        this._clearTimers();
        if (!options || !options.silent) window.scrollTo({ top: 0 });
      }

      /*
        The quiz id goes on <html> beside the open flag so a stylesheet can reach page
        chrome that lives OUTSIDE the overlay — the footer, an app embed — for one quiz
        and not the others. The overlay element carries `data-nq-quiz-id`, but nothing
        outside it is a descendant of the overlay, and CSS cannot select an ancestor's
        sibling from a descendant.
      */
      _setOpen(isOpen) {
        if (isOpen) {
          document.documentElement.setAttribute(OPEN_ATTRIBUTE, 'true');
          document.documentElement.setAttribute(ID_ATTRIBUTE, this.dataset.nqQuizId || '');
        } else {
          document.documentElement.removeAttribute(OPEN_ATTRIBUTE);
          document.documentElement.removeAttribute(ID_ATTRIBUTE);
        }
      }

      /* -------------------------------------------------------------- restore */

      /** Reopening mid-session keeps the answers so Back still works. */
      _restore() {
        var saved = window.NorseQuiz ? window.NorseQuiz.read() : null;
        if (!saved || saved.completed) return;
        this.answers = saved.answers || {};
        this.tokens = saved.tokens || {};
        // A run saved before the questions were edited can hold answers this quiz no
        // longer offers, which would leave a branch selected by a value nothing shows.
        this._pruneAnswers();
        this._repaintSelections();

        /*
          Back from the signup redirect: reopen on the analysing screen the shopper was
          already looking at, rather than at the cover. The marker is consumed straight
          away — a manual refresh from here is somebody starting over, and it must not
          fast-forward them past the questions.
        */
        if (typeof saved.signupResumeIndex === 'number') {
          this.index = Math.max(0, Math.min(saved.signupResumeIndex, this.steps.length - 1));
          this.signupResumeIndex = null;

          /*
            How much of the analysing screen the shopper has already watched — the wait
            for the redirect counts, because what matters is the wall time they spent
            looking at it. `_runLoading` shows the remainder, and if there is none left
            the overlay never opens: the page paints on the results, which is what should
            happen when the round trip took longer than the screen itself.
          */
          var step = this.steps[this.index];
          var shownAt = typeof saved.signupShownAt === 'number' ? saved.signupShownAt : 0;
          this.signupShownAt = null;
          if (step && step.dataset.nqType === 'loading' && shownAt) {
            var duration = parseInt(step.dataset.nqDuration || '2000', 10);
            var elapsed = Math.max(0, Date.now() - shownAt);
            if (elapsed >= duration - RESUME_MIN_MS) this.resumeStraightToResults = true;
            else this.resumeElapsedMs = elapsed;
          }

          this._persist(false);
          this._cleanSignupUrl();
        }
      }

      /*
        Shopify appends `?customer_posted=true` (and its own fragment) on the way back.
        Dropping it keeps a refresh or a shared link clean, and `replaceState` leaves no
        extra history entry for the back button to land on.
      */
      _cleanSignupUrl() {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        if (!url.searchParams.has('customer_posted')) return;
        url.searchParams.delete('customer_posted');
        url.hash = '';
        window.history.replaceState(null, '', url.pathname + (url.search || '') );
      }

      _persist(completed) {
        if (!window.NorseQuiz) return;
        window.NorseQuiz.write({
          answers: this.answers,
          labels: this._labels(),
          tokens: this.tokens,
          completed: !!completed,
          quizId: this.dataset.nqQuizId || '',
          // Set only while the signup redirect is in flight; see `_submitEmail`.
          signupResumeIndex: typeof this.signupResumeIndex === 'number' ? this.signupResumeIndex : null,
          signupShownAt: typeof this.signupShownAt === 'number' ? this.signupShownAt : null,
          updatedAt: Date.now(),
        });
      }

      /**
       * The human-readable label behind every stored value, in the order the
       * shopper picked them. The results page needs it for chips and for
       * headlines that name the answer, and deriving it there would mean
       * duplicating the option copy outside the quiz.
       */
      _labels() {
        var map = {};
        var quiz = this;

        Object.keys(this.answers).forEach(function (questionId) {
          /*
            Every step carrying the id, not the first one. A branched question has two
            and they share the id, so the option the shopper picked lives on only one
            of them — taking the first would fall through to the raw value on whichever
            branch happened to be rendered second.
          */
          var steps = quiz.querySelectorAll('[data-nq-question="' + questionId + '"]');
          if (steps.length === 0) return;

          map[questionId] = (quiz.answers[questionId] || []).map(function (value) {
            for (var i = 0; i < steps.length; i += 1) {
              var option = steps[i].querySelector('[data-nq-value="' + value + '"]');
              if (!option) continue;
              var label = option.querySelector('.norse-quiz__card-title, .norse-quiz__option-label');
              return (label || option).textContent.trim();
            }
            return value;
          });
        });

        return map;
      }

      /**
       * The run as two things the Shopify admin can hold: a readable Q-and-A summary
       * for the customer's Note, and one tag per answer for segmenting on.
       *
       * Built from the steps the shopper actually saw (`_countedSteps`), so a branched
       * question contributes the half that was shown and the skipped half contributes
       * nothing — reading `this.answers` alone would lose the question text, and reading
       * every step would print both wordings of Q5 and Q7.
       *
       * The question text is the RENDERED title, not the block's setting: the three-voice
       * copy is swapped through `textContent` at show time, so this is the sentence the
       * shopper was actually asked.
       */
      _answerSummary(prefix) {
        var quiz = this;
        var labels = this._labels();
        var lines = [];
        var tags = [];

        function slug(value) {
          return String(value)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }

        this._countedSteps().forEach(function (step) {
          var questionId = step.dataset.nqQuestion;
          var chosen = quiz.answers[questionId] || [];
          if (!questionId || chosen.length === 0) return;

          var titleNode = step.querySelector('.norse-quiz__title');
          var question = titleNode ? titleNode.textContent.trim() : step.getAttribute('aria-label') || questionId;
          var given = labels[questionId] && labels[questionId].length ? labels[questionId] : chosen;

          lines.push(question + ' ' + given.join(', '));

          chosen.forEach(function (value) {
            /*
              A Shopify tag cannot contain a comma — it is the separator the field is
              split on — so everything is slugged rather than escaped. The prefix keeps
              three quizzes' answers apart when one shopper takes more than one.
            */
            var tag = [prefix, questionId, value].filter(Boolean).map(slug).join('-');
            if (tag && tags.indexOf(tag) === -1) tags.push(tag);
          });
        });

        var header = (this.dataset.nqQuizId || 'quiz') + ' — ' + new Date().toISOString().slice(0, 10);
        var note = lines.length ? header + '\n' + lines.join('\n') : '';

        // Shopify does not document a limit on the note and this is the only thing
        // writing to it, but a runaway value would fail the whole signup rather than
        // truncate, so it is capped here instead.
        if (note.length > 2000) note = note.slice(0, 2000);

        return { note: note, tags: tags };
      }

      /* ---------------------------------------------------------- eligibility */

      /**
       * A step carrying `data-nq-show-when` only takes part in the run while the
       * answers so far satisfy it, which is how one question asks itself two ways:
       * the concern quiz ships an acne Q5 and an ageing Q5 and shows one.
       *
       * The grammar is the results page's `data-nq-when`, and so is the
       * implementation — `NorseQuiz.matches` is a pure function, so it can be handed
       * this element's live answers instead of the persisted ones. That difference is
       * the whole reason the results page's own attribute cannot be used here: it is
       * applied once, from what is in sessionStorage.
       *
       * Write the branches so that exactly one is always eligible, including before
       * the question they depend on has been answered — a negated clause is true for
       * an unanswered question, so `!concern:wrinkles` is the "everything else, and
       * nothing yet" half. Otherwise a shopper who skips the source question skips
       * every branch of it too.
       */
      _eligible(step) {
        var expression = step.dataset.nqShowWhen;
        if (!expression) return true;
        if (!window.NorseQuiz || !window.NorseQuiz.matches) return true;
        return window.NorseQuiz.matches(expression, this.answers);
      }

      /** The next/previous step this shopper is meant to see, or -1 past the end. */
      _seek(from, direction) {
        for (var i = from + direction; i >= 0 && i < this.steps.length; i += direction) {
          if (this._eligible(this.steps[i])) return i;
        }
        return -1;
      }

      /**
       * Drop answers no eligible step offers any more.
       *
       * Going back and changing the source of a branch leaves the answer to the half
       * that is now hidden behind — a stale `severity` would reach the results page
       * and pick the wrong bundle, and would leave Continue enabled on a question
       * showing nothing selected.
       *
       * The test is the VALUE, not the step. Both halves of a branched question share
       * one `question_id` on purpose, so the results page reads a single key whichever
       * way the shopper went; pruning by "is this step still eligible" would delete
       * the answer the eligible half had just stored. An answer survives while some
       * eligible step still offers it.
       */
      _pruneAnswers() {
        var quiz = this;
        var offered = {};

        this.steps.forEach(function (step) {
          var questionId = step.dataset.nqQuestion;
          if (!questionId || !quiz._eligible(step)) return;
          offered[questionId] = offered[questionId] || [];
          Array.prototype.forEach.call(step.querySelectorAll('[data-nq-option]'), function (option) {
            offered[questionId].push(option.dataset.nqValue);
          });
        });

        Object.keys(this.answers).forEach(function (questionId) {
          var allowed = offered[questionId];
          if (!allowed) {
            delete quiz.answers[questionId];
            return;
          }
          var kept = (quiz.answers[questionId] || []).filter(function (value) {
            return allowed.indexOf(value) !== -1;
          });
          if (kept.length) quiz.answers[questionId] = kept;
          else delete quiz.answers[questionId];
        });
      }

      /* ----------------------------------------------------------- navigation */

      _show(index) {
        this._clearTimers();
        this.index = Math.max(0, Math.min(index, this.steps.length - 1));

        this.steps.forEach(function (step, i) {
          step.dataset.nqActive = i === this.index ? 'true' : 'false';
        }, this);

        var step = this.steps[this.index];
        // Published so CSS can drop the chrome the cover and loading screens
        // do without, instead of each snippet re-styling the shell.
        this.dataset.nqStepType = step.dataset.nqType || '';
        this._updateProgress(step);
        this._updateCounter(step);
        this._updateBackVisibility();
        this._updateSkipVisibility(step);
        this._applyCopyVariant(step);
        this._applyStepConditions(step);
        this._syncContinue(step);
        this.scrollTop = 0;

        this._runTestimonials(step);
        if (step.dataset.nqType === 'loading') this._runLoading(step);
        var focusTarget = step.querySelector('[data-nq-autofocus]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      next() {
        var target = this._seek(this.index, 1);
        if (target === -1) {
          this._complete();
          return;
        }
        this.history.push(this.index);
        this._show(target);
      }

      back() {
        /*
          A popped index is normally eligible — it was visited, so it was. The loop is
          for the case where the answer that made it eligible has since been pruned,
          which would otherwise strand the shopper on a dead branch.
        */
        while (this.history.length > 0) {
          var target = this.history.pop();
          if (this._eligible(this.steps[target])) {
            this._show(target);
            return;
          }
        }
      }

      _updateBackVisibility() {
        var atStart = this.history.length === 0;
        if (this.barBack) this.barBack.dataset.nqHidden = atStart ? 'true' : 'false';
        this.steps.forEach(function (step) {
          var back = step.querySelector('[data-nq-back]');
          if (back) back.dataset.nqHidden = atStart ? 'true' : 'false';
        });
      }

      /*
        A step opts out of the skip arrow with `data-nq-no-skip`. The last question
        sets it: skipping from there lands on the results with nothing answered.
        The rule lives in the Liquid, which knows the step's position; this only
        mirrors it onto the mobile bar's arrow, since that one is shared by every
        step instead of belonging to the step.
      */
      _updateSkipVisibility(step) {
        if (!this.barSkip) return;
        this.barSkip.dataset.nqHidden = step.dataset.nqNoSkip === 'true' ? 'true' : 'false';
      }

      /** The counted questions this shopper will see, branches resolved. */
      _countedSteps() {
        return this.questionSteps.filter(this._eligible, this);
      }

      _updateProgress(step) {
        if (!this.progressBar) return;

        var counted = this._countedSteps();
        var position = counted.indexOf(step);
        var total = counted.length || 1;
        var percent;

        if (step.dataset.nqProgress) {
          percent = parseFloat(step.dataset.nqProgress);
        } else if (position === -1) {
          // Email, loading and results sit after the questions: keep the bar full.
          percent = counted.length && this.index > 0 ? 100 : 0;
        } else {
          percent = ((position + 1) / total) * 100;
        }

        this.progressBar.style.width = Math.max(0, Math.min(100, percent)) + '%';
        if (this.progressWrap) {
          this.progressWrap.setAttribute('aria-valuenow', String(Math.round(percent)));
        }
      }

      /**
       * "5 of 7" over the steps this shopper will actually see.
       *
       * The Liquid renders this too, from the block's static position among every
       * question block — right for a quiz with no branches, and one too many for one
       * with them (the concern quiz has nine question blocks and shows seven). The
       * server-side text is what a visitor with JavaScript blocked keeps, so it stays;
       * this only corrects it once the branches are known.
       */
      _updateCounter(step) {
        if (step.dataset.nqCounts !== 'true') return;

        var counted = this._countedSteps();
        var position = counted.indexOf(step);
        if (position === -1) return;

        var text = this.counterLabel
          .replace('[current]', String(position + 1))
          .replace('[total]', String(counted.length));

        // Two of them per question: the top row's and the heading's. Only one is
        // ever visible, and which one depends on the breakpoint.
        Array.prototype.forEach.call(step.querySelectorAll('[data-nq-counter]'), function (node) {
          node.textContent = text;
        });
      }

      /**
       * In-step branches, evaluated against the live answers on every show. Used for
       * the tip that changes with the concern — the same screen, a different note.
       */
      _applyStepConditions(step) {
        if (!window.NorseQuiz || !window.NorseQuiz.matches) return;
        var quiz = this;
        Array.prototype.forEach.call(step.querySelectorAll('[data-nq-if]'), function (node) {
          node.hidden = !window.NorseQuiz.matches(node.getAttribute('data-nq-if'), quiz.answers);
        });
      }

      /* -------------------------------------------------- three-state copy swap */

      /**
       * Scope 2 asks one question three ways depending on who the shopper is
       * buying for. Each title ships as a full sentence rather than a word swap,
       * because "your teen's" needs a possessive that a token replace gets wrong.
       */
      _applyCopyVariant(step) {
        var sourceId = this.dataset.nqVariantQuestion;
        if (!sourceId) return;

        var chosen = (this.answers[sourceId] || [])[0];
        if (!chosen) return;

        /*
          No `if (!node.dataset.nqVariant) return` guard here. `data-nq-variant` is
          written as a bare attribute, so its value is the empty string — the guard
          was falsy on every node and the three-voice copy never switched at all.
          The selector already restricts this to nodes that carry the attribute.
        */
        var nodes = step.querySelectorAll('[data-nq-variant]');
        var key = 'nqVariant' + chosen.charAt(0).toUpperCase() + chosen.slice(1);
        Array.prototype.forEach.call(nodes, function (node) {
          var text = node.dataset[key];
          if (text) node.textContent = text;
        });
      }

      /* ------------------------------------------------------------ selection */

      _onClick(event) {
        var option = event.target.closest('[data-nq-option]');
        if (option && this.contains(option)) {
          this._select(option);
          return;
        }

        if (event.target.closest('[data-nq-back]')) {
          event.preventDefault();
          this.back();
          return;
        }

        var continueButton = event.target.closest('[data-nq-continue]');
        if (continueButton) {
          /*
            The email step's Continue IS the form's submit control, and it carries
            `data-nq-continue` so it inherits the same styling and keyboard handling as
            every other step's. Calling preventDefault() on the click of a submit button
            cancels the form submission outright, which is exactly what used to happen:
            the step advanced from here and `_onSubmit` never fired, so `_submitEmail`
            never ran — no first name, no email, no customer, no tag. Let the submit
            through and let `_submitEmail` do the advancing after it has validated and
            stored what the shopper typed.
          */
          var continueForm = continueButton.type === 'submit' ? continueButton.closest('form') : null;
          if (continueForm && this.contains(continueForm) && continueForm.querySelector('[name="contact[email]"]')) {
            return;
          }
          event.preventDefault();
          this.next();
          return;
        }

        if (event.target.closest('[data-nq-skip]')) {
          event.preventDefault();
          this._skip();
          return;
        }

        if (event.target.closest('[data-nq-close]')) {
          event.preventDefault();
          this.close();
        }
      }

      _onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        var option = event.target.closest('[data-nq-option]');
        if (!option || !this.contains(option)) return;
        event.preventDefault();
        this._select(option);
      }

      _select(option) {
        var step = option.closest('[data-nq-step]');
        if (!step) return;

        var questionId = step.dataset.nqQuestion;
        var value = option.dataset.nqValue;
        var multi = step.dataset.nqSelection === 'multi';
        var current = (this.answers[questionId] || []).slice();

        if (!multi) {
          this.answers[questionId] = [value];
          // This answer may be what selects a branch further on, so an answer stored
          // for the half that is now hidden has to go before anything reads it.
          this._pruneAnswers();
          this._repaintSelections();
          this._persist(false);

          if (step.dataset.nqAutoAdvance !== 'false') {
            this._defer(function () {
              this.next();
            }, AUTO_ADVANCE_MS);
          }
          return;
        }

        var exclusive = (step.dataset.nqExclusive || '')
          .split(',')
          .map(function (item) {
            return item.trim();
          })
          .filter(Boolean);
        var position = current.indexOf(value);

        if (position !== -1) {
          current.splice(position, 1);
        } else if (exclusive.indexOf(value) !== -1) {
          // "Nothing yet" clears the rest, and picking anything else clears it.
          current = [value];
        } else {
          current = current.filter(function (item) {
            return exclusive.indexOf(item) === -1;
          });

          var max = parseInt(step.dataset.nqMax || '0', 10);
          if (max > 0 && current.length >= max) {
            // Two ways to be full. `block` refuses the tap outright and leaves the
            // shopper to deselect something first — _repaintStep dims the options that
            // stopped responding, so the refusal is visible rather than silent.
            if (step.dataset.nqMaxBehaviour === 'block') return;
            current.shift();
          }
          current.push(value);
        }

        this.answers[questionId] = current;
        this._pruneAnswers();
        this._repaintStep(step);
        this._syncContinue(step);
        this._persist(false);

        if (step.dataset.nqAdvanceAtMax === 'true') {
          var limit = parseInt(step.dataset.nqMax || '0', 10);
          // Only on the way UP to the limit: deselecting from a full set would
          // otherwise leave the shopper on a screen that advances as they edit it.
          if (limit > 0 && current.length >= limit && position === -1) {
            this._defer(function () {
              this.next();
            }, MAX_ADVANCE_MS);
          }
        }
      }

      _repaintStep(step) {
        var chosen = this.answers[step.dataset.nqQuestion] || [];
        var options = step.querySelectorAll('[data-nq-option]');

        // `aria-disabled` rather than `disabled`: a blocked option is still reachable
        // by keyboard and still announced, which is what lets someone discover they
        // have to deselect one first. _select is what actually refuses the tap.
        var max = parseInt(step.dataset.nqMax || '0', 10);
        var blocking = step.dataset.nqMaxBehaviour === 'block' && max > 0 && chosen.length >= max;

        Array.prototype.forEach.call(options, function (option) {
          var isOn = chosen.indexOf(option.dataset.nqValue) !== -1;
          option.setAttribute('aria-checked', String(isOn));
          if (blocking && !isOn) option.setAttribute('aria-disabled', 'true');
          else option.removeAttribute('aria-disabled');
        });
      }

      _repaintSelections() {
        this.steps.forEach(function (step) {
          if (step.dataset.nqQuestion) this._repaintStep(step);
        }, this);
      }

      _syncContinue(step) {
        var button = step.querySelector('[data-nq-continue]');
        if (!button) return;
        if (step.dataset.nqRequired === 'false') return;

        var chosen = this.answers[step.dataset.nqQuestion] || [];
        button.disabled = step.dataset.nqSelection === 'multi' && chosen.length === 0;
      }

      _skip() {
        var step = this.steps[this.index];
        if (step && step.dataset.nqQuestion) delete this.answers[step.dataset.nqQuestion];
        this._persist(false);
        this.next();
      }

      /* --------------------------------------------------------- email capture */

      /*
        The form is identified by the field it must carry, not by the class passed to
        `{% form 'customer' %}`: anything that re-renders or wraps that form would
        silently turn this back into a native submit, and a native submit here means the
        shopper leaves the quiz mid-flow.

        `stopImmediatePropagation` is what keeps the submission ours. Without it the bot
        protection's own handler still runs after this one and takes the shopper to
        `?customer_posted=true` — which also drops `?view=` in preview.
      */
      _onSubmit(event) {
        var form = event.target.closest('form');
        if (!form || !this.contains(form)) return;
        if (!form.querySelector('[name="contact[email]"]')) return;
        /*
          The one submit that must go through: the signup this element fires itself.
          hCaptcha only fills its token during a real submission, and its own handler
          has to see this event to do it — so no preventDefault and no
          stopImmediatePropagation on this pass.
        */
        if (this.signupPending) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this._submitEmail(form);
      }

      /**
       * Submitted through Shopify's own `{% form 'customer' %}` markup, which is what
       * creates the customer, marks them as accepting marketing and applies the
       * merchant's tag — the same mechanism the theme's newsletter sections use, and
       * what Klaviyo picks up on sync.
       *
       * It is a REAL submit and the page navigates. It cannot be a fetch: with hCaptcha
       * enabled, Shopify answers a submission carrying no valid token with 400 (see the
       * 2024-10-28 "Breaking Changes to CAPTCHA protection on Storefront forms"
       * changelog), and the token is only filled in during an actual submission —
       * `window.Shopify.captcha.protect()` adds the `h-captcha-response` field but
       * leaves it empty, measured. So every background attempt was a 400 and no
       * customer was ever created.
       *
       * The navigation is hidden instead of avoided: the analysing screen is painted
       * first, the submit goes out from behind it, and `signupResumeIndex` brings the
       * quiz back to that same screen when Shopify redirects here. What the shopper sees
       * is one analysing screen that happens to take a moment longer.
       */
      _submitEmail(form) {
        var error = form.querySelector('[data-nq-error]');
        var emailInput = form.querySelector('[name="contact[email]"]');
        var nameInput = form.querySelector('[name="contact[first_name]"]');
        var submit = form.querySelector('[data-nq-continue]');
        var email = emailInput ? emailInput.value.trim() : '';
        var name = nameInput ? nameInput.value.trim() : '';

        if (error) error.hidden = true;

        if (!email || email.indexOf('@') === -1) {
          if (error) {
            error.textContent = error.dataset.nqInvalid || 'Enter a valid email address.';
            error.hidden = false;
          }
          if (emailInput) emailInput.focus();
          return;
        }

        if (name) this.tokens.first_name = name;
        this.tokens.email = email;
        this._persist(false);

        // Consent gates the signup, never the results: an unchecked box still
        // personalises the page from what they typed, it just does not subscribe.
        var consent = form.querySelector('[data-nq-consent]');
        if (consent && !consent.checked) {
          track('quiz_email_captured', { quiz_id: this.dataset.nqQuizId || '', subscribed: false });
          this.next();
          return;
        }

        if (submit) submit.disabled = true;

        /*
          `return_to` is a documented parameter of the Liquid form tag, and the snippet
          sets it to `back`. It is rewritten here with the query string as well, because
          Shopify's own redirect keeps only the path — which is what drops `?view=` and
          throws a preview onto the page's real template.
        */
        var returnTo = form.querySelector('[name="return_to"]');
        if (returnTo) returnTo.value = window.location.pathname + window.location.search;

        /*
          The answers ride out with the signup. Filled here rather than kept up to date
          as the shopper goes, because this is the only moment the whole run is known and
          the only moment it is about to be sent.

          Gated on the tag input carrying a prefix, which the snippet only renders when
          the merchant has turned "Save the answers to the customer profile" on — so an
          unconfigured quiz posts exactly the payload it always did.
        */
        var tagsInput = form.querySelector('[data-nq-tags]');
        var prefix = tagsInput ? tagsInput.dataset.nqTagPrefix : '';
        if (prefix) {
          var summary = this._answerSummary(prefix);

          var noteInput = form.querySelector('[data-nq-answer-note]');
          if (noteInput) noteInput.value = summary.note;

          if (summary.tags.length) {
            // Appended, never replacing: the quiz's own tag is already in there and is
            // what the reporting hangs off.
            var existing = tagsInput.value ? [tagsInput.value] : [];
            tagsInput.value = existing.concat(summary.tags).join(',');
          }
        }

        /*
          Marks the submit as ours so `_onSubmit` lets this one reach the browser, and
          records where to come back to. Persisted BEFORE the submit: after it, this
          document is gone.
        */
        this.signupPending = true;
        this.signupResumeIndex = this.index + 1;
        /*
          When the analysing screen went up. hCaptcha decides how long it holds the
          submit, so the redirect can land anywhere — before the screen has finished or
          well after. Carrying the start across the reload lets the other side show only
          what is LEFT of it, which is the difference between one analysing screen and
          watching the same animation twice.
        */
        this.signupShownAt = Date.now();
        this._persist(false);
        track('quiz_email_captured', { quiz_id: this.dataset.nqQuizId || '', subscribed: true });

        // Paint the analysing screen, then submit from behind it.
        this.next();
        this._defer(function () {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }, SIGNUP_SUBMIT_MS);
      }

      /* ---------------------------------------------------------- testimonials */

      /*
        Keeps the current quote centred by sliding the track, so the neighbours spill
        evenly past both edges of the column the way the design draws them. Measuring
        offsetLeft/offsetWidth rather than stepping by a fixed amount, because the
        quotes are different widths and `justify-content: center` already positions
        the row — the translate is only ever the delta between the row's centre and
        the current quote's centre.

        Only the visible step's strip runs. Every step is in the DOM at once, so a
        timer per strip would keep nine of them ticking behind the one on screen.
      */
      _runTestimonials(step) {
        if (this.testimonialTimer) {
          window.clearInterval(this.testimonialTimer);
          this.testimonialTimer = null;
        }

        var strip = step.querySelector('[data-nq-testimonials]');
        if (!strip) return;

        var track = strip.querySelector('[data-nq-quotes-track]');
        var quotes = strip.querySelectorAll('[data-nq-quote]');
        if (!track || quotes.length === 0) return;

        var index = 0;
        for (var i = 0; i < quotes.length; i += 1) {
          if (quotes[i].dataset.nqCurrent === 'true') index = i;
        }

        function centre() {
          var current = quotes[index];
          var delta = track.offsetWidth / 2 - (current.offsetLeft + current.offsetWidth / 2);
          track.style.transform = 'translateX(' + delta + 'px)';
        }

        function show(next) {
          quotes[index].dataset.nqCurrent = 'false';
          index = (next + quotes.length) % quotes.length;
          quotes[index].dataset.nqCurrent = 'true';
          centre();
        }

        centre();

        var interval = parseInt(strip.dataset.nqInterval || '0', 10);
        if (quotes.length < 2 || !interval) return;

        var quiz = this;
        this.testimonialTimer = window.setInterval(function () {
          // A hidden tab reports zero widths, which would centre on nothing.
          if (document.hidden || !quiz.isConnected) return;
          show(index + 1);
        }, interval);
      }

      /* --------------------------------------------------------------- loading */

      /*
        The bar runs to 92% over most of the duration and then crawls the last 8%
        over `tail_ms`, so it reads as work finishing rather than a timer expiring.
        Two transitions rather than one eased curve: `ease-out` slows the whole
        second half, which makes the screen feel slow instead of making the *end*
        feel considered, and it cannot be held at a specific percentage.

        `linear` on both legs is deliberate — the seam between them is where the
        deceleration reads, and an eased leg would blur it.
      */
      _runLoading(step) {
        var duration = parseInt(step.dataset.nqDuration || '2000', 10);
        var tail = parseInt(step.dataset.nqTail || '0', 10);
        var fill = step.querySelector('[data-nq-loading-fill]');

        // A tail longer than the duration would leave no room for the first leg.
        tail = Math.max(0, Math.min(tail, duration - 200));
        var lead = duration - tail;
        var LEAD_PERCENT = 92;

        /*
          Resuming after the signup redirect starts part-way through: the bar picks up
          where it left off rather than snapping back to zero, and each leg gets only the
          time it has left. `resumeElapsedMs` is consumed here — a later run of this
          screen is a new one.
        */
        var elapsed = Math.max(0, this.resumeElapsedMs || 0);
        this.resumeElapsedMs = null;
        var remaining = Math.max(0, duration - elapsed);
        var leadLeft = Math.max(0, lead - elapsed);
        var tailLeft = remaining - leadLeft;
        var startPercent = 0;
        if (elapsed > 0) {
          startPercent = elapsed < lead
            ? (LEAD_PERCENT * elapsed) / lead
            : LEAD_PERCENT + ((100 - LEAD_PERCENT) * Math.min(1, (elapsed - lead) / (tail || 1)));
        }

        if (fill) {
          fill.style.transitionDuration = leadLeft + 'ms';
          fill.style.width = startPercent + '%';
          // Next frame, so the browser has a width to animate from.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              fill.style.transitionDuration = (tailLeft > 0 ? leadLeft : remaining) + 'ms';
              fill.style.width = tailLeft > 0 ? LEAD_PERCENT + '%' : '100%';
            });
          });

          if (tailLeft > 0) {
            this._defer(function () {
              fill.style.transitionDuration = tailLeft + 'ms';
              fill.style.width = '100%';
            }, leadLeft);
          }
        }

        /*
          While the signup submit is in flight this screen must not advance to the
          results underneath it — the page is about to navigate. It still gets a timer,
          far longer than any redirect, so a submission that never leaves ends with the
          shopper on their results instead of stuck here.
        */
        this._defer(function () {
          this.next();
        }, this.signupPending ? SIGNUP_HOLD_MS : remaining);
      }

      /* -------------------------------------------------------------- complete */

      _complete() {
        // Finishing here means the redirect never happened (the hold expired); the
        // resume marker has nothing left to point at.
        this.signupPending = false;
        this.signupResumeIndex = null;
        this._persist(true);
        track('quiz_completed', {
          quiz_id: this.dataset.nqQuizId || '',
          answers: this.answers,
        });

        document.dispatchEvent(
          new CustomEvent('norse:quiz-complete', {
            detail: { answers: this.answers, tokens: this.tokens },
            bubbles: true,
          })
        );

        this.close();
      }

      /* ---------------------------------------------------------------- timers */

      _defer(callback, delay) {
        var quiz = this;
        var id = window.setTimeout(function () {
          callback.call(quiz);
        }, delay);
        this.timers.push(id);
      }

      _clearTestimonials() {
        if (!this.testimonialTimer) return;
        window.clearInterval(this.testimonialTimer);
        this.testimonialTimer = null;
      }

      _clearTimers() {
        this.timers.forEach(window.clearTimeout);
        this.timers = [];
      }
    }
  );
})();
